import { useEffect, useRef, useState } from "react";

// Fetches only while `enabled` (a layer that needs the data is shown) and
// refreshes every `refreshMs` while it stays enabled. A failed fetch is
// retried sooner, after `retryMs`. Switching a layer back on reuses data that
// is still fresh instead of fetching again.
export const usePolling = (fetcher, enabled, { refreshMs, retryMs }) => {
  const [state, setState] = useState({
    data: null,
    fetchedAt: null,
    error: null,
  });
  const fetchedAt = useRef(0);
  // Data only counts as fresh for the fetcher that produced it; a new fetcher
  // (e.g. other postcodes) fetches straight away
  const fetchedWith = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer;

    const load = () => {
      fetcher()
        .then((data) => {
          if (cancelled) return;
          fetchedAt.current = Date.now();
          fetchedWith.current = fetcher;
          setState({ data, fetchedAt: fetchedAt.current, error: null });
          timer = setTimeout(load, refreshMs);
        })
        .catch((error) => {
          if (cancelled) return;
          console.error(error);
          setState((current) => ({ ...current, error }));
          timer = setTimeout(load, retryMs);
        });
    };

    const age = Date.now() - fetchedAt.current;
    const fresh = fetchedWith.current === fetcher;
    timer = setTimeout(load, fresh ? Math.max(0, refreshMs - age) : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fetcher, enabled, refreshMs, retryMs]);

  return state;
};
